/* eslint-disable @reatom/unit-naming-rule */
import { action, atom, withAssign } from '@reatom/framework'

import { AsyncStateN, pipe, Result } from '@blade/std-lib'
import { currencyRegistry } from '@root/entities/currency/atoms'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { premiumMetaRegistry } from '@root/entities/premium'
import { reatomResource } from '@root/shared/lib'

import { fetchUserPremiumCurrenciesRequestAction } from './premium-requests'

const reatomUserPremiums = (network: Network.Network) => {
  const stateAtom = reatomResource(
    async (ctx) => {
      const result = await ctx.schedule(() =>
        fetchUserPremiumCurrenciesRequestAction(ctx, { network }),
      )

      const mappedResult = pipe(
        result,
        Result.map((it) =>
          it.map((dto) =>
            premiumMetaRegistry.resolve(ctx, {
              dto: dto.premium,
              currency: currencyRegistry.resolveFromTokenMeta(ctx, dto),
            }),
          ),
        ),
      )

      return mappedResult
    },
    {
      reconnectStrategy: 'refresh',
      retry: {
        enabled: true,
      },
    },
  )

  const activePremiumsAtom = atom((ctx) => {
    const state = ctx.spy(stateAtom)

    return AsyncStateN.map(state, (it) =>
      it.filter((meta) => {
        return ctx.spy(meta.expiresAt.isFuture)
      }),
    )
  })

  const expiredPremiumsAtom = atom((ctx) => {
    const state = ctx.spy(stateAtom)

    return AsyncStateN.map(state, (it) =>
      it.filter((meta) => {
        return ctx.spy(meta.expiresAt.isPast)
      }),
    )
  })

  return stateAtom.pipe(
    withAssign(() => ({
      activePremiums: activePremiumsAtom,
      expiredPremiums: expiredPremiumsAtom,
    })),
  )
}

export const refreshUserPremiumsAction = action((ctx) => {
  const model = ctx.get(userPremiumsModelAtom)
  model.refresh(ctx)
})
export type UserPremiumsReatom = ReturnType<typeof reatomUserPremiums>

const userPremiumsModelByNetwork = Network.byNetworkRecord((network) =>
  reatomUserPremiums(network),
)

export const userPremiumsModelAtom = atom((ctx) => {
  const network = ctx.spy(selectedNetworkAtom)
  return userPremiumsModelByNetwork[network]
}, 'userPremiumsModelAtom')

export const userPremiumActiveStateAtom = atom((ctx) => {
  const userPremiumsStateAtom = ctx.spy(userPremiumsModelAtom)
  return ctx.spy(userPremiumsStateAtom.activePremiums)
}, 'userPremiumActiveStateAtom')

export const userPremiumExpiredStateAtom = atom((ctx) => {
  const userPremiumsStateAtom = ctx.spy(userPremiumsModelAtom)
  return ctx.spy(userPremiumsStateAtom.expiredPremiums)
}, 'userPremiumExpiredStateAtom')
